import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { ContasAPagarService } from '../../services/dashboard/contas-a-pagar.service';
import { ListaReceitasDespesasDTO } from '../../models/dashboard/lista-receitas-despesas.model';
import { TotalMoedaDTO } from '../../models/dashboard/lista-receitas-despesas.model';
import { ContaService } from '../../services/conta/conta.service';
import { TipoDespesaService } from '../../services/enums/tipo-despesa.service';
import { PeriodoFiltro } from '../../models/dashboard/periodo-filtro.model';
import { calcularPeriodoPredefinido, fmtDateTime } from '../../shared/utils/periodo.utils';
import { PaginatorState } from 'primeng/paginator';

interface CardTotal {
  simbolo: string;
  valor: number;
  label: string;
  corClasse: string;
}

@Component({
  selector: 'app-contas-a-pagar',
  templateUrl: './contas-a-pagar.component.html',
  styleUrls: ['./contas-a-pagar.component.scss'],
})
export class ContasAPagarComponent implements OnInit, OnDestroy {

  // --- Dados ---
  dados: ListaReceitasDespesasDTO[] = [];
  totais: TotalMoedaDTO | null = null;
  totalRecords = 0;

  // --- Paginacao ---
  first = 0;
  rows = 10;
  page = 0;

  // --- Filtros ---
  tipoDespesaSelecionada: string | null = null;
  contaSelecionada: number | null = null;
  searchDescricao = '';
  pessoaId: number | null = null;
  filtroMoedaSelecionada: string | null = null;

  // --- Dados brutos (para filtro local por moeda) ---
  dadosOriginal: ListaReceitasDespesasDTO[] = [];

  // --- Periodo (navegacao por mes) ---
  mesAtual: Date = new Date();
  periodoSelecionado!: PeriodoFiltro;
  usandoPeriodoCustomizado = false;

  // --- Opcoes dos filtros ---
  tipoDespesaOptions: { label: string; value: string }[] = [];
  contas: { label: string; value: number; flagUrl?: string }[] = [];

  // --- Loading ---
  loading = false;
  loadingTotais = false;
  loadingContas = false;
  loadingTipos = false;

  // --- Cards de totais ---
  cardsTotais: CardTotal[] = [];

  // --- Debounce ---
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  // --- Traducoes ---
  texts = {
    titulo: '',
    subtitulo: '',
  };

  labels = {
    id: '',
    descricao: '',
    data_emissao: '',
    emissao: '',
    valor: '',
    pessoa: '',
    tipo_despesa: '',
    total_guarani: '',
    total_real: '',
    total_dolar: '',
    todas_as_contas: '',
    todos_os_tipos: '',
    nenhum_resultado: '',
    moeda: '',
    lista_despesas: '',
  };

  placeholders = {
    buscar_descricao: '',
  };

  private langChangeSub!: Subscription;

  constructor(
    private contasAPagarService: ContasAPagarService,
    private contaService: ContaService,
    private tipoDespesaService: TipoDespesaService,
    private messageService: MessageService,
    private translate: TranslateService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    // Extrair pessoaId do queryParam
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const pid = params['pessoaId'];
      this.pessoaId = pid ? Number(pid) : null;
    });

    this.loadTranslations();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });

    // Debounce da busca por descricao
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
    ).subscribe(() => {
      this.onFiltroChange();
    });

    // Periodo padrao inicial: "Mostrar todos" (showAll), alinhado ao mobile
    this.aplicarPeriodoPadrao();

    // Carregar contas do dropdown
    this.carregarContas();

    // Carregar tipos de despesa
    this.carregarTiposDespesa();

    // Carregar dados iniciais
    this.carregarDados();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  // --- Traducoes ---

  private loadTranslations(): void {
    this.texts = {
      titulo: this.translate.instant('contas-a-pagar.texts.titulo'),
      subtitulo: this.translate.instant('contas-a-pagar.texts.subtitulo'),
    };

    this.labels = {
      id: this.translate.instant('contas-a-pagar.labels.id'),
      descricao: this.translate.instant('contas-a-pagar.labels.descricao'),
      data_emissao: this.translate.instant('contas-a-pagar.labels.data_emissao'),
      emissao: this.translate.instant('contas-a-pagar.labels.emissao'),
      valor: this.translate.instant('contas-a-pagar.labels.valor'),
      pessoa: this.translate.instant('contas-a-pagar.labels.pessoa'),
      tipo_despesa: this.translate.instant('contas-a-pagar.labels.tipo_despesa'),
      total_guarani: this.translate.instant('contas-a-pagar.labels.total_guarani'),
      total_real: this.translate.instant('contas-a-pagar.labels.total_real'),
      total_dolar: this.translate.instant('contas-a-pagar.labels.total_dolar'),
      todas_as_contas: this.translate.instant('contas-a-pagar.labels.todas_as_contas'),
      todos_os_tipos: this.translate.instant('contas-a-pagar.labels.todos_os_tipos'),
      nenhum_resultado: this.translate.instant('contas-a-pagar.labels.nenhum_resultado'),
      moeda: this.translate.instant('contas-a-pagar.labels.moeda'),
      lista_despesas: this.translate.instant('contas-a-pagar.labels.lista_despesas'),
    };

    this.placeholders = {
      buscar_descricao: this.translate.instant('contas-a-pagar.placeholders.buscar_descricao'),
    };

    // Atualizar cards de totais se ja existem dados
    if (this.totais) {
      this.atualizarCardsTotais();
    }
  }

  // --- Periodo / Navegacao por mes ---

  get mesAnoLabel(): string {
    const locale = this.translate.currentLang === 'es' ? 'es-PY' : 'pt-BR';
    const label = this.mesAtual.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  mesAnterior(): void {
    this.mesAtual = new Date(this.mesAtual.getFullYear(), this.mesAtual.getMonth() - 1, 1);
    this.usandoPeriodoCustomizado = false;
    this.recalcularPeriodo();
    this.carregarDados();
  }

  proximoMes(): void {
    this.mesAtual = new Date(this.mesAtual.getFullYear(), this.mesAtual.getMonth() + 1, 1);
    this.usandoPeriodoCustomizado = false;
    this.recalcularPeriodo();
    this.carregarDados();
  }

  recalcularPeriodo(): void {
    const inicio = new Date(this.mesAtual.getFullYear(), this.mesAtual.getMonth(), 1, 0, 0);
    const fim = new Date(this.mesAtual.getFullYear(), this.mesAtual.getMonth() + 1, 0, 23, 59);
    this.periodoSelecionado = {
      tipo: 'thisMonth',
      dataInicio: fmtDateTime(inicio),
      dataFim: fmtDateTime(fim),
      texto: this.mesAnoLabel,
    };
  }

  /**
   * Define o periodo INICIAL da tela como "Mostrar todos" (showAll), alinhado
   * ao app mobile e ao valorPadrao do app-periodo-filtro. Intervalo fixo
   * 01/01/2000 00:00 -> 31/12/3000 23:59. A navegacao por mes (mesAnterior/
   * proximoMes) continua usando recalcularPeriodo().
   */
  aplicarPeriodoPadrao(): void {
    const inicio = new Date(2000, 0, 1, 0, 0);
    const fim = new Date(3000, 11, 31, 23, 59);
    this.periodoSelecionado = {
      tipo: 'showAll',
      dataInicio: fmtDateTime(inicio),
      dataFim: fmtDateTime(fim),
      texto: this.translate.instant('dashboard.filtros.mostrar_todos'),
    };
  }

  onPeriodoChange(periodo: PeriodoFiltro): void {
    this.periodoSelecionado = periodo;
    this.usandoPeriodoCustomizado = true;
    this.onFiltroChange();
  }

  // --- Carregar contas dropdown ---

  carregarContas(): void {
    this.loadingContas = true;
    this.contaService.get('/conta').subscribe({
      next: (response: any) => {
        this.contas = (response.content || []).map((c: any) => ({
          label: c.numeroConta == 0 ? `${c.tituloDaConta} - EFETIVO` : c.tituloDaConta,
          value: c.id,
          flagUrl: this.getFlagUrlByMoeda(c.moeda),
        }));
        this.loadingContas = false;
      },
      error: () => {
        this.loadingContas = false;
      },
    });
  }

  // --- Carregar tipos de despesa ---

  carregarTiposDespesa(): void {
    this.loadingTipos = true;
    this.tipoDespesaService.get('/enums/tipoDespesa').subscribe({
      next: (response: any) => {
        const tipos = Array.isArray(response) ? response : (response.content || []);
        this.tipoDespesaOptions = tipos.map((t: any) => ({
          label: t.nome,
          value: t.enum || t.nome,
        }));
        this.loadingTipos = false;
      },
      error: () => {
        this.loadingTipos = false;
      },
    });
  }

  getFlagUrlByMoeda(moeda: any): string {
    const moedaEnum = (typeof moeda === 'object' && moeda?.enum ? moeda.enum : moeda || '').toString().toUpperCase();
    switch (moedaEnum) {
      case 'GUARANI': return 'assets/images/paraguay-flag.jpg';
      case 'REAL': return 'assets/images/br-flag.svg';
      case 'DOLAR': return 'assets/images/usa-flag.svg';
      case 'PESO': return 'assets/images/argentina-flag.svg';
      default: return '';
    }
  }

  getContaFlagUrl(contaId: number): string {
    const conta = this.contas.find(c => c.value === contaId);
    return conta?.flagUrl || '';
  }

  getContaLabel(contaId: number): string {
    const conta = this.contas.find(c => c.value === contaId);
    return conta?.label || '';
  }

  // --- Carregar dados ---

  carregarDados(): void {
    this.carregarListagem();
    this.carregarTotais();
  }

  carregarListagem(): void {
    this.loading = true;
    // Se filtro de moeda ativo, buscar todos os registros para filtrar localmente
    const usandoFiltroMoeda = !!this.filtroMoedaSelecionada;
    this.contasAPagarService.listarDespesas({
      page: usandoFiltroMoeda ? 0 : this.page,
      size: usandoFiltroMoeda ? 9999 : this.rows,
      conta: this.contaSelecionada || undefined,
      dataInicio: this.periodoSelecionado.dataInicio,
      dataFim: this.periodoSelecionado.dataFim,
      tipoDespesa: this.tipoDespesaSelecionada || undefined,
      pessoaId: this.pessoaId || undefined,
      observacao: this.searchDescricao || undefined,
    }).subscribe({
      next: (response) => {
        this.dadosOriginal = response.content;
        if (usandoFiltroMoeda) {
          this.aplicarFiltroMoedaLocal();
        } else {
          this.dados = response.content;
          this.totalRecords = response.totalElements;
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro_servidor'),
        });
      },
    });
  }

  // --- Filtro local por moeda ---

  private aplicarFiltroMoedaLocal(): void {
    if (!this.filtroMoedaSelecionada) {
      this.dados = [...this.dadosOriginal];
      this.totalRecords = this.dadosOriginal.length;
      return;
    }

    const moedaFiltro = this.filtroMoedaSelecionada.toUpperCase();
    const filtrados = this.dadosOriginal.filter(d => {
      const moedaDespesa = (typeof d.moedaDeOrigem === 'object' && (d.moedaDeOrigem as any)?.enum
        ? (d.moedaDeOrigem as any).enum
        : d.moedaDeOrigem || '').toString().toUpperCase();
      return moedaDespesa === moedaFiltro;
    });
    this.dados = filtrados;
    this.totalRecords = filtrados.length;
  }

  filtrarPorMoeda(moeda: string): void {
    if (this.filtroMoedaSelecionada === moeda) {
      // Toggle: remover filtro
      this.filtroMoedaSelecionada = null;
    } else {
      this.filtroMoedaSelecionada = moeda;
    }

    this.first = 0;
    this.page = 0;
    this.aplicarFiltroMoedaLocal();
  }

  carregarTotais(): void {
    this.loadingTotais = true;
    this.contasAPagarService.buscarTotais({
      conta: this.contaSelecionada || undefined,
      dataInicio: this.periodoSelecionado.dataInicio,
      dataFim: this.periodoSelecionado.dataFim,
      tipoDespesa: this.tipoDespesaSelecionada || undefined,
      pessoaId: this.pessoaId || undefined,
      observacao: this.searchDescricao || undefined,
    }).subscribe({
      next: (totais) => {
        this.totais = totais;
        this.atualizarCardsTotais();
        this.loadingTotais = false;
      },
      error: () => {
        this.loadingTotais = false;
      },
    });
  }

  // --- Cards de totais ---

  private atualizarCardsTotais(): void {
    if (!this.totais) return;

    this.cardsTotais = [
      {
        simbolo: 'G$',
        valor: this.totais.valorTotalEmGuarani,
        label: this.labels.total_guarani,
        corClasse: 'total-card-primary',
      },
      {
        simbolo: 'R$',
        valor: this.totais.valorTotalEmReal,
        label: this.labels.total_real,
        corClasse: 'total-card-success',
      },
      {
        simbolo: '$',
        valor: this.totais.valorTotalEmDolar,
        label: this.labels.total_dolar,
        corClasse: 'total-card-info',
      },
    ];
  }

  // --- Eventos de filtros ---

  onFiltroChange(): void {
    this.first = 0;
    this.page = 0;
    this.carregarDados();
  }

  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchDescricao = value;
    this.searchSubject.next(value);
  }

  // --- Paginacao ---

  onPageChange(event: PaginatorState): void {
    this.first = event.first ?? 0;
    this.rows = event.rows ?? 10;
    this.page = Math.floor(this.first / this.rows);
    this.carregarListagem();
  }

  // --- Helpers de formatacao ---

  getMoedaSimbolo(moeda: any): string {
    const moedaStr = (typeof moeda === 'object' && moeda?.enum ? moeda.enum : moeda || '').toString().toUpperCase();
    if (!moedaStr) return '';
    switch (moedaStr) {
      case 'GUARANI': return 'G$';
      case 'REAL': return 'R$';
      case 'DOLAR': return '$';
      default: return '';
    }
  }

  getTipoDespesaLabel(tipoDespesa: any): string {
    if (!tipoDespesa) return '-';
    if (typeof tipoDespesa === 'string') return tipoDespesa;
    return tipoDespesa.descricao || tipoDespesa.nome || tipoDespesa.tipoEnum || '-';
  }

  getTipoDespesaIcone(tipoDespesa: any): string {
    if (!tipoDespesa || typeof tipoDespesa === 'string') return '';
    return tipoDespesa.icone || '';
  }

  getTipoDespesaCor(tipoDespesa: any): string {
    if (!tipoDespesa || typeof tipoDespesa === 'string') return '';
    return tipoDespesa.cor || '';
  }

  formatarEmissao(emissao: string | null): string {
    if (!emissao) return '-';
    try {
      const date = new Date(emissao);
      if (isNaN(date.getTime())) return emissao;
      const lang = this.translate.currentLang || 'pt-BR';
      const locale = lang === 'es' ? 'es-PY' : 'pt-BR';
      return date.toLocaleString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return emissao;
    }
  }
}
